import { isPlainObject, merge } from 'lodash';

import { numberAxisLabel } from './helpers';
import {
  CHART_COLORS,
  CHART_FONT,
  CHART_HOVER_MARKER_SIZE,
  CHART_INK,
  CHART_SHADOW
} from './constants';

const axisLabel = {
  color: CHART_INK.label,
  fontSize: 11,
  fontWeight: 500,
  fontFamily: CHART_FONT
};

// Every number axis is comma-grouped by default, so a chart only opts out by passing its own
// formatter (currencyAxisLabel and friends still win, since chart options override the theme).
const numberAxisLabelStyle = { ...axisLabel, formatter: numberAxisLabel };

const hiddenAxisChrome = {
  line: { enabled: false },
  tick: { enabled: false }
};

const hoverOnlyMarker = {
  enabled: true,
  size: 0,
  strokeWidth: 2,
  stroke: CHART_INK.surface,
  formatter: ({ highlighted, size }) => ({ size: highlighted ? CHART_HOVER_MARKER_SIZE : size })
};

export const BASE_CHART_THEME = {
  palette: {
    fills: CHART_COLORS,
    strokes: CHART_COLORS
  },
  overrides: {
    common: {
      animation: { enabled: true, duration: 800 },
      background: { visible: false },
      padding: { top: 12, right: 12, bottom: 8, left: 8 },
      tooltip: { showArrow: false, delay: 0, range: 'nearest' },
      title: {
        color: CHART_INK.value,
        fontSize: 14,
        fontWeight: 600,
        fontFamily: CHART_FONT
      },
      axes: {
        number: {
          ...hiddenAxisChrome,
          gridLine: { style: [{ stroke: CHART_INK.grid, lineDash: [4, 4] }] },
          label: numberAxisLabelStyle,
          crosshair: { enabled: false }
        },
        category: {
          ...hiddenAxisChrome,
          gridLine: { enabled: false },
          label: axisLabel,
          crosshair: { enabled: false },
          paddingInner: 0.45,
          paddingOuter: 0.2,
          groupPaddingInner: 0.2
        },
        time: {
          ...hiddenAxisChrome,
          gridLine: { enabled: false },
          label: axisLabel,
          crosshair: {
            enabled: true,
            snap: true,
            stroke: '#A4A7AE',
            strokeWidth: 1,
            strokeOpacity: 0.7,
            lineDash: [4, 4],
            label: { enabled: false }
          }
        }
      },
      legend: {
        position: 'bottom',
        spacing: 12,
        preventHidingAll: true,
        item: {
          marker: { shape: 'circle', size: 6, strokeWidth: 0 },
          paddingX: 16,
          label: {
            color: CHART_INK.legendLabel,
            fontSize: 12,
            fontWeight: 500,
            fontFamily: CHART_FONT
          }
        }
      }
    },
    line: {
      series: {
        strokeWidth: 2.5,
        marker: hoverOnlyMarker,
        highlightStyle: {
          item: { stroke: CHART_INK.surface, strokeWidth: 2 },
          series: { dimOpacity: 0.25, strokeWidth: 3 }
        }
      }
    },
    bar: {
      series: {
        cornerRadius: 6,
        strokeWidth: 0,
        highlightStyle: { series: { dimOpacity: 0.35 } }
      }
    },
    area: {
      series: {
        strokeWidth: 2.5,
        fillOpacity: 0.14,
        marker: hoverOnlyMarker,
        shadow: CHART_SHADOW.area,
        highlightStyle: { series: { dimOpacity: 0.3 } }
      }
    },
    scatter: {
      series: {
        marker: { size: 8, strokeWidth: 2, stroke: CHART_INK.surface },
        highlightStyle: { series: { dimOpacity: 0.3 } }
      }
    },
    donut: {
      series: {
        sectorSpacing: 4,
        cornerRadius: 6,
        strokeWidth: 0,
        innerRadiusRatio: 0.72,
        shadow: CHART_SHADOW.ring,
        calloutLabel: { enabled: false },
        innerLabels: { fontFamily: CHART_FONT, color: CHART_INK.value }
      }
    },
    pie: {
      series: {
        sectorSpacing: 4,
        cornerRadius: 6,
        strokeWidth: 0,
        shadow: CHART_SHADOW.ring,
        calloutLabel: { enabled: false }
      }
    }
  }
};

export const mergeChartOptions = (options = {}) => {
  const widgetTheme = isPlainObject(options.theme) ? options.theme : {};
  const merged = { ...options, theme: merge({}, BASE_CHART_THEME, widgetTheme) };

  if (options.series?.length === 1 && !options.legend) {
    merged.legend = { enabled: false };
  }

  return merged;
};
